import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import {
  prettyDate,
  prettyDateShort,
  combinedNotes,
  DEFAULT_SETTINGS,
} from "@/lib/equi";
import BreedingCardDialog from "@/components/BreedingCardDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, FileText, Loader2, Search } from "lucide-react";

const COLS = [
  { key: "date", label: "Date" },
  { key: "left_ovary", label: "L. Ovary" },
  { key: "right_ovary", label: "R. Ovary" },
  { key: "fluid", label: "Fluid" },
  { key: "edema", label: "Edema" },
  { key: "cervix", label: "Cervix" },
  { key: "notes", label: "Notes" },
  { key: "next_check", label: "Next Check" },
];

export default function MareHistory() {
  const [mares, setMares] = useState([]);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [mareId, setMareId] = useState("");
  const [year, setYear] = useState(new Date().getFullYear());
  const currentYear = new Date().getFullYear();
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cardOpen, setCardOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [m, s] = await Promise.all([api.get("/mares"), api.get("/settings")]);
        setMares(m.data);
        setSettings(s.data);
      } catch {
        toast.error("Could not load mares");
      }
    })();
  }, []);

  const load = useCallback(async () => {
    if (!mareId) {
      setHistory(null);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(`/mares/${mareId}/records/year/${year}`);
      setHistory(data);
    } catch {
      toast.error("Could not load history");
    } finally {
      setLoading(false);
    }
  }, [mareId, year]);

  useEffect(() => {
    load();
  }, [load]);

  const currentMare = mares.find((m) => m.id === mareId) || null;
  const mq = query.trim().toLowerCase();
  const matches = mq ? mares.filter((m) => m.name.toLowerCase().includes(mq)) : mares;

  const selectMare = (m) => {
    setMareId(m.id);
    setQuery(m.name);
    setPickerOpen(false);
  };

  const reloadAll = async () => {
    try {
      const m = await api.get("/mares");
      setMares(m.data);
    } catch {
      /* ignore */
    }
    load();
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Mare History</h1>
          <p className="text-muted-foreground text-sm">Every recorded exam for a mare, by season.</p>
        </div>
        <Button onClick={() => setCardOpen(true)} disabled={!currentMare} className="gap-2" data-testid="history-print-card-btn">
          <FileText className="h-4 w-4" /> Breeding Card
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative w-64" data-testid="history-mare-search-wrap">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPickerOpen(true); }}
            onFocus={() => setPickerOpen(true)}
            onBlur={() => setTimeout(() => setPickerOpen(false), 150)}
            placeholder="Mare Name"
            className="pl-9"
            data-testid="history-mare-search"
          />
          {pickerOpen && (
            <div className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto rounded-lg border border-border bg-card shadow-lg" data-testid="history-mare-results">
              {matches.length === 0 ? (
                <div className="px-3 py-2 text-sm text-muted-foreground">No mares match</div>
              ) : (
                matches.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => selectMare(m)}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors ${m.id === mareId ? "bg-accent/50 font-semibold" : ""}`}
                    data-testid={`history-mare-option-${m.id}`}
                  >
                    {m.name}
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-1 bg-card border border-border rounded-lg p-1" data-testid="history-year-toggle">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setYear((y) => y - 1)} data-testid="history-prev-year">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <button
            type="button"
            onClick={() => setYear((y) => y - 1)}
            className="px-3 py-1 rounded-md font-bold font-mono text-sm text-muted-foreground hover:bg-muted transition-colors"
            data-testid={`history-year-chip-${year - 1}`}
          >
            {year - 1}
          </button>
          <span className="text-muted-foreground font-bold">·</span>
          <span
            className="px-3 py-1 rounded-md font-bold font-mono text-sm bg-primary text-primary-foreground"
            data-testid="history-year-label"
          >
            {year}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            disabled={year >= currentYear}
            onClick={() => setYear((y) => y + 1)}
            data-testid="history-next-year"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {history && (
          <span className="text-sm text-muted-foreground">
            {history.total} record{history.total === 1 ? "" : "s"} · {history.filled} with entries
          </span>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : !mareId ? (
        <div className="text-center py-20 bg-card border border-dashed border-border rounded-xl" data-testid="history-pick-mare">
          <p className="text-lg font-semibold">Select a mare to view her history</p>
          <p className="text-muted-foreground text-sm mt-1">Search by name above, then pick a mare from the list.</p>
        </div>
      ) : !history || history.records.length === 0 ? (
        <div className="text-center py-20 bg-card border border-dashed border-border rounded-xl">
          <p className="text-lg font-semibold">No records for {year}</p>
          <p className="text-muted-foreground text-sm mt-1">Add this mare to a day in Daily Rounds to start her history.</p>
        </div>
      ) : (
        <div className="border border-[#C4C8D6] rounded-md bg-card overflow-x-auto equi-scroll">
          <table className="w-full text-[13px] min-w-[760px]">
            <thead>
              <tr className="bg-primary text-primary-foreground text-[11px] uppercase tracking-wide [&>th]:border-r [&>th]:border-white/15 last:[&>th]:border-r-0">
                {COLS.map((c) => (
                  <th key={c.key} className="px-3 py-3 text-left font-semibold whitespace-nowrap">{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {history.records.map((r) => (
                <tr key={r.id} className="border-b border-[#C4C8D6] hover:bg-primary/5 [&>td]:border-r [&>td]:border-[#C4C8D6] last:[&>td]:border-r-0" data-testid={`history-row-${r.id}`}>
                  {COLS.map((c) => {
                    let val = r[c.key] || "";
                    if (c.key === "date") val = prettyDate(r.date);
                    else if (c.key === "notes") val = combinedNotes(r);
                    else if (c.key === "next_check" && val) val = prettyDateShort(val);
                    return (
                      <td
                        key={c.key}
                        className={`px-3 py-3 ${c.key === "date" ? "font-semibold whitespace-nowrap" : ""} ${
                          c.key === "notes" ? "italic text-muted-foreground max-w-[240px]" : ""
                        } ${c.key === "next_check" ? "text-primary font-semibold whitespace-nowrap" : ""}`}
                      >
                        {val || <span className="text-border">—</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <BreedingCardDialog
        open={cardOpen}
        onOpenChange={setCardOpen}
        mare={currentMare}
        year={year}
        settings={settings}
        onSaved={reloadAll}
      />
    </div>
  );
}
